/* ==========================================================================
   Voile Chic — Cart Drawer behaviour
     - Recommendations: inline variant picker, swatch selection, quick add
     - Unlock announcement when a reward tier is crossed
     - Free gifts reconciled in as few requests as possible

   Reconciliation runs only when the cart has actually changed, and asks for
   rendered section HTML on its final request alone — so one shopper action
   costs one repaint rather than four.

   On discounts: Shopify keeps ONE discount code per session, and the Ajax
   cart API has no field for codes. Gifts should be priced by AUTOMATIC
   discounts in admin, which stack. A code on a tier is a single-code
   fallback, retried at most twice per distinct cart state.
   ========================================================================== */

(function () {
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var UNLOCK_KEY = 'vcUnlockTier';
  var DECLINED_KEY = 'vcDeclinedGifts';
  var unlockTimer = null;

  var busy = false;
  var lastFingerprint = '';   /* only the most recent state is kept */
  var codeTries = {};
  var observer = null;
  var writing = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function scroller() {
    return document.querySelector('.wt-cart__drawer__body');
  }

  /* ---------- Pending state ---------------------------------------------- */
  /* Marks the reward bar and the totals while figures are being settled, so
     prices never appear to change on their own. */

  function setPending(state) {
    var marks = document.querySelectorAll('.vc-rw, .wt-cart__footer__body');
    for (var i = 0; i < marks.length; i++) {
      marks[i].classList.toggle('is-reconciling', !!state);
    }
  }

  /* ---------- Rendering -------------------------------------------------- */

  function suspend() {
    writing = true;
  }

  function resume() {
    /* Let the mutation queue drain before listening again. */
    setTimeout(function () {
      writing = false;
    }, 80);
  }

  function refresh(json) {
    var el = drawerEl();
    if (!el || !json || !json.sections || typeof el.renderContents !== 'function') {
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
      return;
    }

    var box = scroller();
    var top = box ? box.scrollTop : 0;

    suspend();
    el.renderContents({ sections: json.sections, items: json.items }, false);

    requestAnimationFrame(function () {
      if (top) {
        var newBox = scroller();
        if (newBox) newBox.scrollTop = top;
      }
      resume();
    });
  }

  /* withSections: only the final request in a chain needs rendered HTML. */
  function post(path, payload, withSections) {
    if (withSections) {
      payload.sections = SECTIONS;
      payload.sections_url = window.location.pathname;
    }
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json();
    });
  }

  function list(attr, el) {
    return (el.getAttribute(attr) || '')
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function sessionGet(key) {
    try {
      return sessionStorage.getItem(key) || '';
    } catch (e) {
      return '';
    }
  }

  function sessionSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable */
    }
  }

  function declined() {
    return sessionGet(DECLINED_KEY).split(',').filter(Boolean);
  }

  function decline(id) {
    var all = declined();
    if (all.indexOf(id) === -1) {
      all.push(id);
      sessionSet(DECLINED_KEY, all.join(','));
    }
  }

  /* A gift the shopper removes stays removed for the session. */
  document.addEventListener(
    'click',
    function (event) {
      var remove = event.target.closest('cart-remove-button');
      if (!remove) return;

      var row = remove.closest('.wt-cart__item--gift');
      if (!row) return;

      var input = row.querySelector('[data-quantity-variant-id]');
      if (input) decline(String(input.getAttribute('data-quantity-variant-id')));
    },
    true
  );

  /* ---------- Tier unlocked announcement --------------------------------- */

  function hideUnlock(el) {
    if (!el) return;
    el.classList.remove('is-visible');
    clearTimeout(unlockTimer);
    setTimeout(function () {
      if (!el.classList.contains('is-visible')) el.setAttribute('hidden', '');
    }, 280);
  }

  function checkUnlock() {
    var el = document.querySelector('.vc-unlock');
    var current = el ? parseInt(el.getAttribute('data-vc-unlock-tier') || '0', 10) : 0;
    var stored = parseInt(sessionGet(UNLOCK_KEY) || '0', 10) || 0;

    if (!el) {
      if (stored !== 0) sessionSet(UNLOCK_KEY, '0');
      return;
    }

    if (current > stored) {
      sessionSet(UNLOCK_KEY, String(current));
      el.removeAttribute('hidden');
      requestAnimationFrame(function () {
        el.classList.add('is-visible');
      });

      var ms = parseInt(el.getAttribute('data-vc-unlock-ms') || '4000', 10);
      clearTimeout(unlockTimer);
      if (ms > 0) {
        unlockTimer = setTimeout(function () {
          hideUnlock(el);
        }, ms);
      }
    } else {
      if (current !== stored) sessionSet(UNLOCK_KEY, String(current));
      el.setAttribute('hidden', '');
    }
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-vc-unlock-close]')) {
      event.preventDefault();
      hideUnlock(document.querySelector('.vc-unlock'));
    }
  });

  /* ---------- Variant picker --------------------------------------------- */

  function closePicker(item) {
    if (!item) return;
    var panel = item.querySelector('[data-vc-variants]');
    var toggle = item.querySelector('[data-vc-choose]');
    if (panel) panel.setAttribute('hidden', '');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    item.classList.remove('is-open');
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-vc-choose]');
    if (!toggle) return;

    event.preventDefault();

    var item = toggle.closest('[data-vc-reco]');
    var panel = item && item.querySelector('[data-vc-variants]');
    if (!panel) return;

    if (toggle.dataset.variantId) {
      addSelected(toggle);
      return;
    }

    var isOpen = !panel.hasAttribute('hidden');

    document.querySelectorAll('[data-vc-reco]').forEach(function (other) {
      if (other !== item) closePicker(other);
    });

    if (isOpen) {
      closePicker(item);
    } else {
      panel.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      item.classList.add('is-open');
    }
  });

  document.addEventListener('click', function (event) {
    var option = event.target.closest('[data-vc-select]');
    if (!option) return;

    event.preventDefault();

    var item = option.closest('[data-vc-reco]');
    if (!item) return;

    var title = option.getAttribute('data-variant-title') || '';
    var image = option.getAttribute('data-variant-image') || '';
    var variantId = option.getAttribute('data-variant-id');

    item.querySelectorAll('[data-vc-select]').forEach(function (other) {
      other.classList.toggle('is-selected', other === option);
      other.setAttribute('aria-pressed', other === option ? 'true' : 'false');
    });

    var chosen = item.querySelector('[data-vc-chosen]');
    if (chosen) {
      chosen.textContent = title;
      chosen.removeAttribute('hidden');
    }

    var thumb = item.querySelector('[data-vc-thumb]');
    if (thumb && image) thumb.setAttribute('src', image);

    var toggle = item.querySelector('[data-vc-choose]');
    if (toggle) {
      toggle.dataset.variantId = variantId;
      toggle.classList.remove('is-added');
      var label = toggle.querySelector('[data-vc-label]');
      var atc = toggle.getAttribute('data-atc-label') || 'Add to Cart';
      if (label) label.textContent = atc;
      toggle.setAttribute('aria-label', atc + ' ' + title);
    }
  });

  /* ---------- Adding to the cart ----------------------------------------- */

  function addSelected(button) {
    if (button.classList.contains('is-loading') || button.classList.contains('is-added')) return;

    var variantId = button.dataset.variantId || button.getAttribute('data-variant-id');
    if (!variantId) return;

    var label = button.querySelector('[data-vc-label]');
    var addedText = button.getAttribute('data-added-label') || 'Added';

    button.classList.add('is-loading');

    post('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] }, true)
      .then(function (json) {
        button.classList.remove('is-loading');
        if (json && json.status) return;
        button.classList.add('is-added');
        if (label) label.textContent = addedText;
        refresh(json);
      })
      .catch(function () {
        button.classList.remove('is-loading');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-vc-quick-add]');
    if (!button) return;
    event.preventDefault();
    addSelected(button);
  });

  /* ---------- Free gift reconciliation ----------------------------------- */

  function fingerprint(cart, earned) {
    var lines = (cart.items || [])
      .map(function (line) {
        return line.variant_id + 'x' + line.quantity + '@' + line.final_line_price;
      })
      .join(',');
    return lines + '|' + earned.join(',');
  }

  function syncGifts() {
    if (busy) return;

    var bar = document.querySelector('.vc-rw[data-vc-gift-autoadd="true"]');
    if (!bar) return;

    var earned = list('data-vc-gift-variants', bar).filter(function (id) {
      return declined().indexOf(id) === -1;
    });
    var codes = list('data-vc-gift-codes', bar);

    busy = true;

    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var print = fingerprint(cart, earned);

        /* Idle guard: nothing has moved since the last pass. */
        if (print === lastFingerprint) return null;
        lastFingerprint = print;

        var items = cart.items || [];
        var updates = {};
        var changed = false;
        var seen = [];
        var unpriced = [];

        items.forEach(function (line) {
          var id = String(line.variant_id);
          var isGift = !!(line.properties && line.properties._vc_gift);

          if (earned.indexOf(id) !== -1) {
            seen.push(id);
            if (line.quantity !== 1) {
              updates[id] = 1;
              changed = true;
            }
            if (line.final_line_price !== 0) unpriced.push(id);
          } else if (isGift) {
            updates[id] = 0;
            changed = true;
          }
        });

        var missing = earned.filter(function (id) {
          return seen.indexOf(id) === -1;
        });

        var wantsCode = false;
        if (unpriced.length && codes.length) {
          var sig = codes[0];
          var tries = codeTries[print + '|' + sig] || 0;
          if (tries < 2) {
            codeTries[print + '|' + sig] = tries + 1;
            wantsCode = true;
          }
        }

        if (!changed && !missing.length && !wantsCode) return null;

        setPending(true);

        /* Chain the work, asking for rendered sections on the last call only. */
        var steps = [];

        if (changed) {
          steps.push(function (isLast) {
            return post('cart/update.js', { updates: updates }, isLast);
          });
        }

        if (missing.length) {
          steps.push(function (isLast) {
            return post(
              'cart/add.js',
              {
                items: missing.map(function (id) {
                  return { id: Number(id), quantity: 1, properties: { _vc_gift: 'true' } };
                })
              },
              isLast
            ).catch(function () {
              return null;
            });
          });
        }

        if (wantsCode) {
          steps.push(function (isLast) {
            return fetch(root() + 'discount/' + encodeURIComponent(codes[0]), {
              method: 'GET',
              credentials: 'same-origin'
            })
              .then(function () {
                return post('cart/update.js', { updates: {} }, isLast);
              })
              .catch(function () {
                return null;
              });
          });
        }

        return steps.reduce(function (chain, step, index) {
          var isLast = index === steps.length - 1;
          return chain.then(function (prev) {
            return step(isLast).then(function (json) {
              return isLast ? json || prev : prev;
            });
          });
        }, Promise.resolve(null));
      })
      .then(function (json) {
        busy = false;
        setPending(false);
        if (json) {
          /* The cart moved, so the guard must not block the next check. */
          lastFingerprint = '';
          refresh(json);
        }
      })
      .catch(function () {
        busy = false;
        setPending(false);
      });
  }

  /* ---------- Run on load and after every drawer re-render --------------- */

  var pending = null;

  function runChecks() {
    if (writing) return;
    clearTimeout(pending);
    pending = setTimeout(function () {
      checkUnlock();
      syncGifts();
    }, 60);
  }

  function observeDrawer() {
    var target = document.getElementById('CartDrawer');
    if (!target || target.dataset.vcObserved) return;
    target.dataset.vcObserved = 'true';

    observer = new MutationObserver(function () {
      if (writing) return;
      runChecks();
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', function () {
    observeDrawer();
    runChecks();
  });

  document.addEventListener('cart-drawer:refresh', runChecks);

  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, runChecks);
  }

  ['cart:updated', 'ajaxProduct:added', 'on:cart:add'].forEach(function (ev) {
    document.addEventListener(ev, runChecks);
  });
})();
